import type {
  ApiNode,
  ApiNodeDataBusinessData,
  ApiNodeDataCollectData,
  ApiNodeDataCollectDocument,
  ApiNodeDataIdentityData,
  ApiNodeDataVerificationCheck,
  ApiNodeInlineContext,
  DecisionStatus,
  ManualReviewKind,
  SetEntityStatusConfig,
  VaultKind,
} from '@onefootprint/request-types/dashboard';
import type { Node as XyNode, NodeProps as XyNodeProps } from '@xyflow/react';

/**
 * `vendor_integration` has no backend counterpart: the backend models a managed-vendor call as an
 * `external_api` node whose source is a vendor integration, and the frontend renders each with its
 * own node component and drawer.
 */
export type NodeTypesKey = ApiNode['kind'] | 'edge_label' | 'vendor_integration';

/**
 * The React Flow node type for one of our kinds. React Flow derives a `.react-flow__node-<type>`
 * class from it and styles four reserved type names (`input`, `default`, `output`, `group`), so an
 * unprefixed kind named `output` would inherit React Flow's own node card on top of ours.
 */
export type ReactFlowNodeType = `node_${NodeTypesKey}`;

export const reactFlowNodeType = (kind: NodeTypesKey): ReactFlowNodeType => `node_${kind}`;

export type GraphDimensions = {
  width: number;
  height: number;
  originX: number;
  originY: number;
};

export type NodeData<T> = T & {
  subject: VaultKind;
  /** The graph from which this node originates. This will update every time an edit operation is applied. */
  graphId: string;
  context?: ApiNodeInlineContext;
};

export type NodeProps<T> = XyNodeProps<XyNode<NodeData<T>>>;

export type CollectDataNodeProps = NodeProps<ApiNodeDataCollectData>;

export type BusinessDataNodeProps = NodeProps<ApiNodeDataBusinessData>;

export type IdentityDataNodeProps = NodeProps<ApiNodeDataIdentityData>;

export type VerificationCheckNodeProps = NodeProps<ApiNodeDataVerificationCheck>;

export type CollectDocumentNodeProps = NodeProps<ApiNodeDataCollectDocument>;

export type ActionType = 'setDecision' | 'tag' | 'note' | 'manualReview' | 'setEntityStatus';

export type FormManualReviewKind = `kind:${ManualReviewKind}` | `reviewKindId:${string}`;

/** The source of a single tag entry in the action form. */
export type TagSource = 'manual' | 'expression';

/**
 * A single tag entry. `manual` holds a literal value; `expression` holds a `{{ token }}` reference
 * to a data field that evaluates to a string or list of strings.
 */
export type ActionFormTag = {
  source: TagSource;
  value: string;
};

export type ActionFormData = {
  visibleActions: ActionType[];
  setDecision: DecisionStatus | '';
  addTags: ActionFormTag[];
  addAnnotations: string;
  raiseManualReview: FormManualReviewKind;
  setEntityStatuses: SetEntityStatusConfig[];
};
